import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PersonaShell from './persona-shell';
import RuleEditorDrawer from './rule-editor';
import {
  RulePayload,
  RuleTurn,
  SandboxMessage,
  SandboxPeersonaMetadata,
  extractSandboxMetadata,
  usePromoteSandbox,
  usePromptVersions,
  useSandboxChat,
} from '../../hooks/usePersonaAdmin';

const MetadataChips: React.FC<{ metadata?: SandboxPeersonaMetadata | null }> = ({ metadata }) => {
  if (!metadata) return null;
  const chips: Array<{ label: string; value: string | number }> = [];
  if (metadata.tier) chips.push({ label: 'tier', value: metadata.tier });
  if (metadata.intent) chips.push({ label: 'intent', value: metadata.intent });
  if (metadata.handoff) chips.push({ label: 'handoff', value: 'yes' });
  if (typeof metadata.top_score === 'number') chips.push({ label: 'top_score', value: metadata.top_score.toFixed(2) });
  if (chips.length === 0) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {chips.map((chip) => (
        <span key={chip.label} className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-800">
          {chip.label}: {chip.value}
        </span>
      ))}
    </div>
  );
};

const PersonaChatPage: React.FC = () => {
  const { personaId } = useParams<{ personaId: string }>();

  const [history, setHistory] = useState<SandboxMessage[]>([]);
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(new Set());
  const [input, setInput] = useState('');
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [selectedPromptVersion, setSelectedPromptVersion] = useState<string>('');

  const chat = useSandboxChat(personaId);
  const promote = usePromoteSandbox(personaId);
  const { data: versionsData } = usePromptVersions(personaId);
  const versions = versionsData?.data || [];

  const toggleSelected = (idx: number) => {
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const send = async () => {
    if (!input.trim()) return;
    const userMessage: SandboxMessage = { role: 'user', content: input.trim() };
    const nextHistory = [...history, userMessage];
    setHistory(nextHistory);
    setInput('');

    try {
      const response = await chat.mutateAsync({
        message: userMessage.content,
        history,
        prompt_version_id: selectedPromptVersion ? Number.parseInt(selectedPromptVersion, 10) : undefined,
      });
      const meta = extractSandboxMetadata(response);
      const assistant: SandboxMessage = {
        role: 'assistant',
        content: response.ai_response,
        peersona: meta,
      };
      setHistory([...nextHistory, assistant]);
    } catch (err) {
      setHistory(history);
    }
  };

  const reset = () => {
    setHistory([]);
    setSelectedIdx(new Set());
  };

  const promoteTurns: RuleTurn[] = useMemo(() => {
    const selected = Array.from(selectedIdx).sort((a, b) => a - b);
    return selected
      .map((idx) => history[idx])
      .filter((msg) => msg && msg.role === 'user')
      .map((msg) => ({ text: msg.content, precondition: '' }));
  }, [history, selectedIdx]);

  const suggestedMeta = useMemo(() => {
    for (let i = history.length - 1; i >= 0; i--) {
      const msg = history[i];
      if (msg.role === 'assistant' && msg.peersona) return msg.peersona;
    }
    return null;
  }, [history]);

  const openPromote = () => {
    if (promoteTurns.length === 0) return;
    setPromoteOpen(true);
  };

  const handlePromoteSubmit = async (payload: RulePayload) => {
    await promote.mutateAsync({
      turns: payload.turns,
      slug: payload.slug,
      notes: payload.notes,
      tags: payload.tags,
      expected_tier: payload.expected_tier,
      expected_intent: payload.expected_intent,
      expected_concierge_subtype: payload.expected_concierge_subtype,
      expected_handoff_target: payload.expected_handoff_target,
    });
    setPromoteOpen(false);
    setSelectedIdx(new Set());
  };

  return (
    <PersonaShell personaId={personaId} tab="chat">
      <div className="mx-auto max-w-4xl space-y-4 p-8">
        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white p-3 shadow-sm">
          <div>
            <div className="text-sm font-semibold text-gray-900">Sandbox Chat</div>
            <div className="text-xs text-gray-500">
              Chat with this Peersona to test rules and prompt versions. Select user turns and promote them into an eval
              rule.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={selectedPromptVersion}
              onChange={(e) => setSelectedPromptVersion(e.target.value)}
              className="rounded border border-gray-300 p-1.5 text-xs focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Active prompt version</option>
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version} ({v.status})
                </option>
              ))}
            </select>
            <button
              className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100"
              onClick={reset}
            >
              Reset
            </button>
            <button
              className="bg-blue-primary rounded px-2 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={openPromote}
              disabled={promoteTurns.length === 0}
            >
              Promote {promoteTurns.length ? `(${promoteTurns.length})` : ''}
            </button>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white shadow-sm">
          <div className="max-h-[60vh] space-y-2 overflow-y-auto p-4">
            {history.length === 0 && (
              <div className="py-8 text-center text-xs text-gray-400">Start the conversation below.</div>
            )}
            {history.map((msg, idx) => {
              const isUser = msg.role === 'user';
              const isSelected = selectedIdx.has(idx);
              return (
                <div key={idx} className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
                  {isUser && (
                    <input
                      type="checkbox"
                      className="mt-2"
                      checked={isSelected}
                      onChange={() => toggleSelected(idx)}
                      title="Include this turn when promoting to a rule"
                    />
                  )}
                  <div
                    className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                      isUser ? 'bg-blue-primary text-white' : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                    {!isUser && <MetadataChips metadata={msg.peersona} />}
                  </div>
                </div>
              );
            })}
            {chat.isLoading && <div className="text-xs text-gray-400">Peersona is typing…</div>}
          </div>

          <div className="flex items-end gap-2 border-t border-gray-200 p-3">
            <textarea
              className="flex-1 resize-none rounded border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              className="bg-blue-primary rounded px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={!input.trim() || chat.isLoading}
              onClick={send}
            >
              {chat.isLoading ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      {promoteOpen && (
        <RuleEditorDrawer
          rule={{
            slug: '',
            turns: promoteTurns,
            tags: [],
            expected_tier: suggestedMeta?.tier || '',
            expected_intent: suggestedMeta?.intent || '',
            expected_concierge_subtype: '',
            expected_handoff_target: suggestedMeta?.handoff ? 'yes' : '',
            enabled: true,
            shared: false,
          }}
          initialTurns={promoteTurns}
          title="Promote to Rule"
          onClose={() => setPromoteOpen(false)}
          onSubmit={handlePromoteSubmit}
          isSubmitting={promote.isLoading}
        />
      )}
    </PersonaShell>
  );
};

export default PersonaChatPage;
